import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';

import { loginStatusVar } from '@context';
import { useHandleFriendMutation, useGetFriendshipQuery } from '@graphql';
import { useSession, useToastError } from '@hooks';

interface FriendRequestButtonProps {
  consumerId: string;
}

export function FriendRequestButton(props: Readonly<FriendRequestButtonProps>) {
  const { consumerId } = props;
  const theme = useTheme();
  const { user, account } = useSession();
  const loginStatus = useReactiveVar(loginStatusVar);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuWidth, setMenuWidth] = React.useState<number | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation('cta');

  const menuSx = {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '8px 16px',
    whiteSpace: 'normal',
  };

  const { data } = useGetFriendshipQuery({
    skip: !consumerId || !account?.id,
    variables: { accountId: consumerId },
  });

  const [handleFriend] = useHandleFriendMutation({
    onError: useToastError,
  });

  if (!data?.getFriendship) {
    return null;
  }

  const { isPending, isAccepted, isSent } = data.getFriendship;

  const handleTryUnfriend = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setMenuWidth(buttonRef.current ? buttonRef.current.offsetWidth : null);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleAddFriend = () => {
    if (!user) {
      return loginStatusVar({
        ...loginStatus,
        shouldLogFirst: true,
      });
    }
    handleFriend({
      variables: { accountId: consumerId, action: 'add' },
    });
  };

  const handlefriendAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    const action = event.currentTarget.dataset.id;

    if (!action) {
      return;
    }

    handleFriend({
      variables: { accountId: consumerId, action },
    });

    handleCloseMenu();
  };

  let buttonLabel = '';

  if (isAccepted) {
    buttonLabel = t('friend');
  } else if (isPending) {
    buttonLabel = t('awaiting acceptance');
  } else {
    buttonLabel = t('ask as friend');
  }

  if (consumerId === account?.id) {
    return null;
  }

  if (isPending && !isSent) {
    return (
      <Stack
        spacing={1}
        p={1}
        width="100%"
        sx={{
          border: `1px solid ${theme.palette.background.paper}`,
          borderRadius: theme.borderRadius,
        }}
      >
        <Typography variant="body2" fontWeight="bold" textAlign="center">
          {t('text.New requests', { ns: 'account' })}
        </Typography>

        <Stack direction="row" spacing={1} width="100%">
          <Button
            data-id="accept"
            startIcon={<theme.icons.check />}
            variant="contained"
            color="primary"
            fullWidth
            onClick={handlefriendAction}
          >
            {t('accept')}
          </Button>
          <Button
            data-id="reject"
            startIcon={<theme.icons.close />}
            variant="contained"
            color="uncolored"
            fullWidth
            onClick={handlefriendAction}
          >
            {t('reject')}
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Button
        fullWidth
        ref={buttonRef}
        variant="contained"
        startIcon={!isAccepted && !isPending ? <theme.icons.add /> : null}
        color={isAccepted || isPending ? 'uncolored' : 'primary'}
        onClick={isAccepted || isPending ? handleTryUnfriend : handleAddFriend}
        endIcon={isAccepted || isPending ? <theme.icons.expandMore /> : null}
      >
        {buttonLabel}
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        elevation={3}
        open={open}
        sx={{ width: '100%', mt: 1 }}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
            style: { width: menuWidth ?? '100%' },
          },
        }}
      >
        <MenuItem
          data-id={isAccepted ? 'remove' : 'cancel'}
          sx={menuSx}
          color="uncolored"
          onClick={handlefriendAction}
        >
          {isAccepted ? t('remove from friends') : t('cancel friend request')}
        </MenuItem>
      </Menu>
    </Box>
  );
}
